import { Button, Stack, TextField } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useTranslation } from "react-i18next";
import { Body } from "../../components/primitives/data-display/Body";
import { Note } from "../../components/primitives/data-display/Note";
import { gaps } from "../../components/primitives/spacing";
import { useNoCoverage } from "./useNoCoverage";

interface CoverageRequestFormProps {
  /** What we ask the person, above the email field. */
  prompt: string;
  /** Sends the request to be told when the claim becomes answerable. */
  submitRequest: (email: string) => Promise<void>;
}

/** Asks for an email to tell when the claim becomes answerable, then confirms it was sent. */
export function CoverageRequestForm({ prompt, submitRequest }: CoverageRequestFormProps) {
  const { t } = useTranslation();
  const { email, setEmail, submitted, submitting, handleSubmit } = useNoCoverage(submitRequest);

  return (
    <Stack component="section" gap={gaps.base}>
      <Body>{prompt}</Body>

      {submitted ? (
        <Stack direction="row" gap={gaps.snug} sx={{ alignItems: "center", color: "success.main" }}>
          <CheckCircleOutlineIcon />
          <Note color="inherit">{t("noCoverage.requestSent", { email })}</Note>
        </Stack>
      ) : (
        <Stack direction="row" gap={gaps.snug}>
          <TextField
            label={t("noCoverage.emailLabel")}
            type="email"
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!email || submitting}
            loading={submitting}
          >
            {t("noCoverage.submit")}
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
